import React, { useState, useEffect, useContext } from "react";
import { FiPlus, FiGrid, FiBarChart2, FiRefreshCw } from "react-icons/fi";
import { UserContext } from "../../../context/UserContext";
import { useRoom } from "../../../context/RoomContext";
import { useRoomTasks } from "../../../context/RoomTasksContext";
import { roomSocketService } from "../../../services/rooms";
import KanbanBoard from "./tasks/KanbanBoard";
import TaskDetailModal from "./tasks/TaskDetailModal";
import CreateTaskModal from "./tasks/CreateTaskModal";
import TaskStatistics from "./tasks/TaskStatistics";
import "./TasksTab.css";

const TasksTab = ({ roomId, userRole }) => {
  const { user } = useContext(UserContext);
  const { currentRoom } = useRoom();
  const {
    kanbanBoard,
    loading,
    fetchKanbanBoard,
    createTask,
    setCurrentRoomId,
  } = useRoomTasks();

  const [view, setView] = useState("kanban");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isLeader = userRole?.isLeader;

  useEffect(() => {
    setCurrentRoomId(roomId);
  }, [roomId, setCurrentRoomId]);

  useEffect(() => {
    if (roomId) {
      fetchKanbanBoard(roomId);
    }
  }, [roomId, fetchKanbanBoard]);

  useEffect(() => {
    if (!roomId) return;

    const handleTaskCreated = (data) => {
      if (data.roomId === roomId) {
        fetchKanbanBoard(roomId);
      }
    };

    const handleTaskUpdated = (data) => {
      if (data.roomId === roomId) {
        fetchKanbanBoard(roomId);
      }
    };

    const handleTaskStatusChanged = (data) => {
      if (data.roomId === roomId) {
        fetchKanbanBoard(roomId);
      }
    };

    const handleTaskDeleted = (data) => {
      if (data.roomId === roomId) {
        fetchKanbanBoard(roomId);
        if (selectedTask && selectedTask._id === data.taskId) {
          setSelectedTask(null);
        }
      }
    };

    const unsubCreated = roomSocketService.on(
      "task_created",
      handleTaskCreated
    );
    const unsubUpdated = roomSocketService.on(
      "task_updated",
      handleTaskUpdated
    );
    const unsubStatusChanged = roomSocketService.on(
      "task_status_changed",
      handleTaskStatusChanged
    );
    const unsubDeleted = roomSocketService.on(
      "task_deleted",
      handleTaskDeleted
    );

    return () => {
      unsubCreated();
      unsubUpdated();
      unsubStatusChanged();
      unsubDeleted();
    };
  }, [roomId, fetchKanbanBoard, selectedTask]);

  const handleCreateTask = async (taskData) => {
    try {
      await createTask(taskData);
      setShowCreateModal(false);
    } catch (error) {
throw error;
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = (updatedTask) => {
    if (selectedTask && selectedTask._id === updatedTask._id) {
      setSelectedTask(updatedTask);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchKanbanBoard(roomId);
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const getTotalTasks = () => {
    if (!kanbanBoard) return 0;
    return Object.values(kanbanBoard).reduce(
      (acc, tasks) => acc + tasks.length,
      0
    );
  };

  const getCompletedTasks = () => {
    if (!kanbanBoard || !kanbanBoard.completed) return 0;
    return kanbanBoard.completed.length;
  };

  return (
    <div className="rtsk-container">
      {/* Header Section */}
      <header className="rtsk-header">
        <div className="rtsk-header-left">
          <div className="rtsk-title-group">
            <h1 className="rtsk-title">Tasks</h1>
            <div className="rtsk-task-count">
              <span className="rtsk-count-completed">
                {getCompletedTasks()}
              </span>
              <span className="rtsk-count-divider">/</span>
              <span className="rtsk-count-total">{getTotalTasks()}</span>
            </div>
          </div>
        </div>

        <div className="rtsk-header-right">
          {/* View Toggle */}
          <div className="rtsk-view-toggle">
            <button
              className={`rtsk-view-btn ${
                view === "kanban" ? "rtsk-view-btn--active" : ""
              }`}
              onClick={() => setView("kanban")}
              title="Board View"
            >
              <FiGrid size={18} />
              <span className="rtsk-view-label">Board</span>
            </button>
            <button
              className={`rtsk-view-btn ${
                view === "stats" ? "rtsk-view-btn--active" : ""
              }`}
              onClick={() => setView("stats")}
              title="Statistics"
            >
              <FiBarChart2 size={18} />
              <span className="rtsk-view-label">Stats</span>
            </button>
          </div>

          {/* Refresh Button */}
          <button
            className={`rtsk-icon-btn ${
              isRefreshing ? "rtsk-icon-btn--spinning" : ""
            }`}
            onClick={handleRefresh}
            title="Refresh"
          >
            <FiRefreshCw size={18} />
          </button>

          {/* Create Task Button */}
          {isLeader && (
            <button
              className="rtsk-create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus size={18} />
              <span>New Task</span>
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="rtsk-main">
        {view === "kanban" && (
          <KanbanBoard
            board={kanbanBoard}
            onTaskClick={handleTaskClick}
            loading={loading}
          />
        )}

        {view === "stats" && <TaskStatistics roomId={roomId} />}
      </main>

      {/* Task Detail Panel */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          roomId={roomId}
          userRole={userRole}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
        />
      )}

      {/* Create Task Modal */}
      {showCreateModal && isLeader && (
        <CreateTaskModal
          roomId={roomId}
          members={currentRoom?.members || []}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateTask}
        />
      )}
    </div>
  );
};

export default TasksTab;
